import React, { useCallback, useEffect, useRef, useState } from "react"
import axios from "axios"
import ExcelJS from "exceljs"
import { saveAs } from "file-saver"
import DataTable from "react-data-table-component"
import CompanySelector from "ATSComponents/Common/CompanySelector"

/* ------------------------------------------------------------------ */
/* constants                                                          */
/* ------------------------------------------------------------------ */

const OFFER_STATUS_OPTIONS = [
  "Pending",
  "Offered",
  "Accepted",
  "Declined",
  "Joined",
  "Dropped",
]

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100]
const DEFAULT_PAGE_SIZE = 10

const fmtDate = v => {
  if (!v) return "--"
  const d = new Date(v)
  if (Number.isNaN(d.getTime())) return "--"
  const day = String(d.getDate()).padStart(2, "0")
  const month = String(d.getMonth() + 1).padStart(2, "0")
  const year = d.getFullYear()
  return `${day}-${month}-${year}`
}

const displayValue = value => {
  const text = value === null || value === undefined ? "" : String(value).trim()
  return text || "--"
}

const toInputDate = v => {
  if (!v) return ""
  const d = new Date(v)
  if (Number.isNaN(d.getTime())) return ""
  return d.toISOString().slice(0, 10)
}

const toApiDate = v => {
  if (!v) return null
  const d = v instanceof Date ? v : new Date(v)
  if (Number.isNaN(d.getTime())) return null
  return d.toISOString().slice(0, 10)
}

/* ------------------------------------------------------------------ */
/* inline style tokens                                                */
/* ------------------------------------------------------------------ */

const colors = {
  primary: "#21A366", // Excel brand green
  primaryDark: "#107C41", // Excel dark green
  primarySoft: "#E6F4EA",
  stripe: "#F1FAF4", // banded-row tint
  success: "#107C41",
  successSoft: "#E6F4EA",
  danger: "#C4314B",
  dangerSoft: "#FDE7EA",
  border: "#D0D7DE",
  bg: "#f5f6fb",
  card: "#ffffff",
  text: "#1f2330",
  textMuted: "#6b7280",
  editable: "#EAF3FB", // light-blue input-cell tint
  editableBorder: "#4A90D9", // light-blue accent
}

const styles = {
  page: {
    fontFamily: "'Inter', 'Segoe UI', -apple-system, sans-serif",
    background: colors.bg,
    minHeight: "100%",
    // padding: "24px 28px 48px",
    color: colors.text,
    position: "relative",
  },
  header: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 16,
    marginBottom: 22,
    flexWrap: "wrap",
    padding: "25px 26px",
    borderRadius: 26,
    background: `linear-gradient(135deg, ${colors.primarySoft} 0%, #ffffff 65%)`,
    border: `1px solid ${colors.border}`,
    boxShadow: "0 6px 20px rgba(16,24,40,0.06)",
  },
  headerLeft: { display: "flex", alignItems: "center", gap: 14 },
  iconBubble: {
    width: 46,
    height: 46,
    minWidth: 46,
    borderRadius: "50%",
    background: `linear-gradient(135deg, ${colors.primary}, ${colors.primaryDark})`,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 20,
    boxShadow: "0 4px 10px rgba(16,124,65,0.35)",
  },
  title: { fontSize: 24, fontWeight: 800, margin: 0, letterSpacing: "-0.02em" },
  subtitle: { fontSize: 13.5, color: colors.textMuted, margin: "6px 0 0" },
  exportBtn: (busy, disabled) => ({
    display: "inline-flex",
    alignItems: "center",
    gap: 8,
    padding: "12px 22px",
    borderRadius: 999,
    border: "none",
    fontSize: 14,
    fontWeight: 600,
    color: "#fff",
    background: disabled
      ? "#a7a9b3"
      : busy
      ? colors.primaryDark
      : colors.success,
    cursor: disabled ? "not-allowed" : "pointer",
    boxShadow: disabled ? "none" : "0 6px 16px rgba(16,124,65,0.3)",
    transition: "background 0.15s ease, transform 0.1s ease",
  }),
  panel: {
    background: colors.card,
    border: `1px solid ${colors.border}`,
    borderRadius: 16,
    padding: "18px 20px 8px",
    boxShadow: "0 1px 3px rgba(16,24,40,0.04)",
  },
  toolbar: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    flexWrap: "wrap",
    marginBottom: 16,
    width: "100%",
  },
  companyWrap: { flex: 1, minWidth: 210 },
  searchWrap: { position: "relative", flex: 1.3, minWidth: 260 },
  searchIcon: {
    position: "absolute",
    left: 12,
    top: "50%",
    transform: "translateY(-50%)",
    color: "#9aa1af",
    fontSize: 14,
    pointerEvents: "none",
  },
  searchInput: {
    width: "100%",
    minHeight: 40,
    padding: "8px 34px 8px 34px",
    borderRadius: 999,
    border: `1px solid ${colors.border}`,
    fontSize: 13.5,
    outline: "none",
    boxSizing: "border-box",
  },
  clearBtn: {
    position: "absolute",
    right: 8,
    top: "50%",
    transform: "translateY(-50%)",
    width: 22,
    height: 22,
    borderRadius: "50%",
    border: "none",
    background: colors.border,
    color: "#fff",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 12,
    lineHeight: 1,
    cursor: "pointer",
  },
  dateInput: {
    minHeight: 40,
    padding: "8px 12px",
    borderRadius: 999,
    border: `1px solid ${colors.border}`,
    fontSize: 13.5,
    outline: "none",
    color: colors.text,
  },
  recruiterSelect: {
    minWidth: 220,
    height: 40,
    borderRadius: 999,
    border: `1px solid ${colors.border}`,
    padding: "0 12px",
    fontSize: 13.5,
    color: colors.text,
    background: "#fff",
    outline: "none",
  },
  multiselectWrapper: {
    position: "relative",
    minWidth: 220,
  },
  multiselectButton: {
    width: "100%",
    minHeight: 40,
    borderRadius: 999,
    border: `1px solid ${colors.border}`,
    padding: "0 12px",
    fontSize: 13.5,
    color: colors.text,
    background: "#fff",
    cursor: "pointer",
    textAlign: "left",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
  },
  caret: {
    marginLeft: 8,
    fontSize: 12,
    display: "none",
  },
  multiselectPanel: {
    position: "absolute",
    top: 48,
    left: 0,
    right: 0,
    maxHeight: 260,
    overflowY: "auto",
    background: "#fff",
    border: `1px solid ${colors.border}`,
    borderRadius: 12,
    boxShadow: "0 10px 30px rgba(0,0,0,0.08)",
    zIndex: 2000,
    padding: 8,
  },
  multiselectItem: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    padding: "8px 10px",
    borderRadius: 10,
    cursor: "pointer",
    fontSize: 13.5,
    color: colors.text,
  },
  selectedJobChips: {
    display: "flex",
    flexWrap: "wrap",
    gap: 6,
    marginTop: 6,
    maxWidth: 300,
  },
  selectedJobChip: {
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    maxWidth: 140,
    background: colors.primarySoft,
    border: `1px solid #b7e4c7`,
    color: colors.primaryDark,
    borderRadius: 999,
    padding: "4px 8px",
    fontSize: 12,
    fontWeight: 700,
  },
  selectedJobChipText: {
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  selectedJobChipRemove: {
    border: "none",
    background: "transparent",
    color: colors.primaryDark,
    cursor: "pointer",
    fontSize: 13,
    fontWeight: 900,
    lineHeight: 1,
    padding: 0,
  },
  spacer: { flex: 1 },
  refreshBtn: spinning => ({
    width: 40,
    height: 40,
    minWidth: 40,
    borderRadius: "50%",
    border: `1px solid ${colors.border}`,
    background: "#fff",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
    fontSize: 16,
    color: colors.primary,
    transition: "transform 0.4s ease",
    transform: spinning ? "rotate(360deg)" : "none",
  }),
  toast: type => ({
    position: "fixed",
    top: 20,
    right: 20,
    zIndex: 1000,
    padding: "12px 18px",
    borderRadius: 10,
    fontSize: 13.5,
    fontWeight: 500,
    color: type === "error" ? colors.danger : colors.success,
    background: type === "error" ? colors.dangerSoft : colors.successSoft,
    border: `1px solid ${type === "error" ? "#fecaca" : "#a7f3d0"}`,
    boxShadow: "0 6px 20px rgba(16,24,40,0.12)",
  }),
  editInput: {
    width: "100%",
    boxSizing: "border-box",
    padding: "6px 8px",
    borderRadius: 6,
    border: `1.5px solid ${colors.editableBorder}`,
    fontSize: 13,
    outline: "none",
  },
  editableCell: {
    width: "100%",
    minHeight: 20,
    padding: "4px 6px",
    background: colors.editable,
    borderRadius: 3,
    borderBottom: `2px solid ${colors.editableBorder}`,
    cursor: "text",
    fontSize: 13,
    boxSizing: "border-box",
  },
  plainCell: { fontSize: 13 },
  link: { color: colors.primary, textDecoration: "none" },
}

/* Excel-style grid: every cell gets a right + bottom hairline so the
   whole table reads like a spreadsheet, with a solid header band. */
const customTableStyles = {
  tableWrapper: {
    style: {
      border: `1px solid ${colors.border}`,
      borderRadius: 12,
    },
  },
  table: { style: { borderCollapse: "collapse" } },
  headRow: {
    style: {
      background: colors.primary,
      minHeight: 44,
      borderBottom: "none",
    },
  },
  headCells: {
    style: {
      color: "#fff",
      fontSize: 12.5,
      fontWeight: 700,
      textTransform: "none",
      paddingLeft: 12,
      paddingRight: 12,
      borderRight: "1px solid rgba(255,255,255,0.25)",
    },
  },
  cells: {
    style: {
      fontSize: 13,
      paddingTop: 8,
      paddingBottom: 8,
      paddingLeft: 12,
      paddingRight: 12,
      borderRight: `1px solid ${colors.border}`,
    },
  },
  rows: {
    style: {
      minHeight: 44,
      borderBottom: `1px solid ${colors.border}`,
    },
    stripedStyle: { backgroundColor: colors.stripe },
  },
  pagination: {
    style: {
      borderTop: `1px solid ${colors.border}`,
      fontSize: 13,
    },
  },
}

/* ------------------------------------------------------------------ */
/* editable cell                                                      */
/* ------------------------------------------------------------------ */

function EditableCell({ value, type = "text", options, onSave }) {
  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState(value ?? "")
  const inputRef = useRef(null)

  useEffect(() => {
    setDraft(value ?? "")
  }, [value])
  useEffect(() => {
    if (editing && inputRef.current) inputRef.current.focus()
  }, [editing])

  const commit = () => {
    setEditing(false)
    const changed =
      type === "date"
        ? toApiDate(draft) !== toApiDate(value)
        : draft !== (value ?? "")
    if (changed) {
      onSave(type === "date" ? toApiDate(draft) : draft)
    }
  }

  const cancel = () => {
    setDraft(value ?? "")
    setEditing(false)
  }

  if (!editing) {
    const display = type === "date" ? fmtDate(value) : displayValue(value)
    return (
      <div
        style={styles.editableCell}
        onClick={() => setEditing(true)}
        title={`${display} - Click to edit`}
      >
        {display || <span style={{ color: "#c6c9d2" }}>—</span>}
      </div>
    )
  }

  const commonProps = {
    ref: inputRef,
    style: styles.editInput,
    onBlur: commit,
    onKeyDown: e => {
      e.stopPropagation()
      if (e.key === "Enter" && type !== "textarea") commit()
      if (e.key === "Escape") cancel()
    },
  }

  if (type === "select") {
    return (
      <select
        {...commonProps}
        value={draft || ""}
        onChange={e => {
          setDraft(e.target.value)
        }}
        onBlur={() => commit()}
      >
        <option value="">Select</option>
        {options.map(o => (
          <option key={o} value={o}>
            {o}
          </option>
        ))}
      </select>
    )
  }
  if (type === "date") {
    return (
      <input
        {...commonProps}
        type="date"
        value={toInputDate(draft)}
        onChange={e => setDraft(e.target.value)}
      />
    )
  }
  if (type === "textarea") {
    return (
      <textarea
        {...commonProps}
        rows={2}
        value={draft || ""}
        style={{ ...styles.editInput, resize: "vertical" }}
        onChange={e => setDraft(e.target.value)}
      />
    )
  }
  return (
    <input
      {...commonProps}
      type="text"
      value={draft || ""}
      onChange={e => setDraft(e.target.value)}
    />
  )
}

/* ------------------------------------------------------------------ */
/* shimmer loading skeleton                                           */
/* ------------------------------------------------------------------ */

const SHIMMER_KEYFRAMES = `
@keyframes shimmerSweep {
  0% { background-position: -400px 0; }
  100% { background-position: 400px 0; }
}
`

const shimmerStyles = {
  bar: (width = "100%", height = 14) => ({
    width,
    height,
    borderRadius: 6,
    background:
      "linear-gradient(90deg, #eef0f4 25%, #f7f8fa 37%, #eef0f4 63%)",
    backgroundSize: "400px 100%",
    animation: "shimmerSweep 1.4s ease-in-out infinite",
  }),
  row: {
    display: "flex",
    alignItems: "center",
    gap: 18,
    padding: "12px 16px",
    borderBottom: `1px solid ${colors.border}`,
  },
}
const SHIMMER_COL_WIDTHS = [
  40, 90, 110, 120, 140, 160, 110, 120, 70, 80, 110, 70, 90, 80, 90, 110, 130,
]

function ShimmerTable({ rowCount = 8 }) {
  return (
    <div>
      <style>{SHIMMER_KEYFRAMES}</style>
      {Array.from({ length: rowCount }).map((_, rowIdx) => (
        <div key={rowIdx} style={shimmerStyles.row}>
          {SHIMMER_COL_WIDTHS.map((w, colIdx) => (
            <div key={colIdx} style={shimmerStyles.bar(w)} />
          ))}
        </div>
      ))}
    </div>
  )
}

/* ------------------------------------------------------------------ */
/* main component                                                     */
/* ------------------------------------------------------------------ */

export default function RecruiterSubmissionReport() {
  const userInfos = JSON.parse(sessionStorage.getItem("user-info") || "{}")
  const currentUserId =
    userInfos?.user_details?.user_id ??
    userInfos?.user_details?.id ??
    userInfos?.user_details?.pk ??
    userInfos?.user_id ??
    userInfos?.id ??
    userInfos?.pk
  const currentUserEmail =
    userInfos?.user_details?.email?.toLowerCase() ??
    userInfos?.email?.toLowerCase() ??
    ""
  const sessionRoleName =
    userInfos?.user_details?.role_name ?? userInfos?.role_name ?? ""
  const sessionUserName =
    [
      userInfos?.user_details?.first_name,
      userInfos?.user_details?.middle_name,
      userInfos?.user_details?.last_name,
    ]
      .filter(Boolean)
      .join(" ")
      .trim() ||
    userInfos?.user_details?.username ||
    userInfos?.user_details?.name ||
    userInfos?.user_details?.email ||
    "Your recruiter"

  const [currentRoleName, setCurrentRoleName] = useState(sessionRoleName)
  const [currentUserName, setCurrentUserName] = useState(sessionUserName)
  const isSuperAdmin =
    String(currentRoleName).toLowerCase() === "superadmin" ||
    String(currentRoleName).toLowerCase() === "super_admin"
  const canSelectMultipleRecruiters = isSuperAdmin

  const initialCompany = userInfos?.user_details?.companies_data?.[0] ?? {}
  const initialCompanyId = initialCompany?.company_id ?? null
  const initialCompanyName =
    initialCompany?.company_name || initialCompany?.name || ""

  const [companyId, setCompanyId] = useState(initialCompanyId)
  const [companyName, setCompanyName] = useState(initialCompanyName)
  const [rows, setRows] = useState([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(false)
  const [exporting, setExporting] = useState(false)
  const [spinning, setSpinning] = useState(false)

  const [page, setPage] = useState(1)
  const [perPage, setPerPage] = useState(DEFAULT_PAGE_SIZE)
  const [search, setSearch] = useState("")
  const [searchInput, setSearchInput] = useState("")
  const [dateFrom, setDateFrom] = useState("")
  const [dateTo, setDateTo] = useState("")
  const [selectedJobIds, setSelectedJobIds] = useState([])
  const [jobOptions, setJobOptions] = useState([])
  const [jobsLoading, setJobsLoading] = useState(false)
  const [jobDropdownOpen, setJobDropdownOpen] = useState(false)
  const jobRef = useRef(null)
  const [selectedRecruiterIds, setSelectedRecruiterIds] = useState([])
  const [rawRecruiterOptions, setRawRecruiterOptions] = useState([])
  const [recruiterOptions, setRecruiterOptions] = useState([])
  const [recruitersLoading, setRecruitersLoading] = useState(false)
  const [recruiterDropdownOpen, setRecruiterDropdownOpen] = useState(false)
  const recruiterRef = useRef(null)
  const recruiterFetchCache = useRef(new Set())

  const [toastMsg, setToastMsg] = useState(null)

  const addDaysToDateString = (dateString, days) => {
    if (!dateString) return ""
    const date = new Date(dateString)
    if (Number.isNaN(date.getTime())) return ""
    date.setDate(date.getDate() + days)
    return date.toISOString().slice(0, 10)
  }

  const notify = (type, message) => {
    setToastMsg({ type, message })
    clearTimeout(notify._t)
    notify._t = setTimeout(() => setToastMsg(null), 3500)
  }

  const loadRecruiters = useCallback(async () => {
    if (!companyId || String(companyId).toLowerCase() === "all") {
      setRawRecruiterOptions([])
      return
    }

    if (recruiterFetchCache.current.has(companyId)) {
      return
    }

    recruiterFetchCache.current.add(companyId)
    setRecruitersLoading(true)
    try {
      const res = await axios.get("/api/v1/ats-users/", {
        params: { company_id: companyId },
      })
      const raw =
        res.data?.results ||
        res.data?.data ||
        res.data?.users ||
        (Array.isArray(res.data) ? res.data : [])
      const options = raw
        .map(user => {
          const id = user.id ?? user.user_id ?? user.pk
          const name = [user.first_name, user.middle_name, user.last_name]
            .filter(Boolean)
            .join(" ")
            .trim()
          if (!id) return null
          return {
            id: String(id),
            label: name || user.username || user.email || "Recruiter",
            email: user.email?.toLowerCase() || "",
          }
        })
        .filter(Boolean)

      setRawRecruiterOptions(options)
    } catch (e) {
      console.error("Could not load recruiters", e)
      setRecruiterOptions([])
    } finally {
      setRecruitersLoading(false)
    }
  }, [companyId])

  const loadJobOrders = useCallback(async () => {
    if (!companyId || String(companyId).toLowerCase() === "all") {
      setJobOptions([])
      return
    }

    setJobsLoading(true)
    try {
      const res = await axios.get("/api/v1/get-all-jobs/", {
        params: {
          company_id: companyId,
          status: "Approved/Open",
        },
      })
      const raw =
        res.data?.results ||
        res.data?.data ||
        res.data?.jobs ||
        (Array.isArray(res.data) ? res.data : [])
      const options = raw
        .map(job => {
          const id = job.job_id ?? job.id ?? job.job_order_id
          if (!id) return null
          return {
            id: String(id),
            label:
              job.job_title ||
              job.title ||
              job.position ||
              job.job_order_id ||
              `Job ${id}`,
          }
        })
        .filter(Boolean)

      setJobOptions(options)
    } catch (error) {
      console.error("Could not load job orders", error)
      setJobOptions([])
    } finally {
      setJobsLoading(false)
    }
  }, [companyId])

  useEffect(() => {
    const filteredOptions = isSuperAdmin
      ? rawRecruiterOptions
      : rawRecruiterOptions.filter(
          opt =>
            String(opt.id) === String(currentUserId) ||
            (currentUserEmail && opt.email === currentUserEmail)
        )

    setRecruiterOptions(filteredOptions)
  }, [currentUserEmail, currentUserId, isSuperAdmin, rawRecruiterOptions])

  const load = useCallback(async () => {
    if (!companyId) return
    setLoading(true)
    try {
      const params = { page, size: perPage }
      if (search) params.search = search
      if (dateFrom) params.date_from = dateFrom
      if (dateTo) params.date_to = dateTo
      if (selectedJobIds.length > 0) {
        params.job_id = selectedJobIds.join(",")
      }
      if (selectedRecruiterIds.length > 0) {
        params.recruiter_id = selectedRecruiterIds.join(",")
      }
      const res = await axios.get(
        `/api/v1/company/${companyId}/recruiter-submission-report/`,
        { params }
      )
      setRows(res.data?.results || [])
      setTotal(res.data?.count || 0)
    } catch (e) {
      notify("error", "Could not load the report.")
    } finally {
      setLoading(false)
    }
  }, [companyId, page, perPage, search, dateFrom, dateTo, selectedJobIds, selectedRecruiterIds])

  useEffect(() => {
    loadRecruiters()
  }, [loadRecruiters])

  useEffect(() => {
    loadJobOrders()
  }, [loadJobOrders])

  useEffect(() => {
    const loadCurrentUserRole = async () => {
      if (!companyId || !currentUserId) return
      try {
        const res = await axios.get("/api/v1/users/", {
          params: {
            page: 1,
            size: 200,
            company_id: companyId,
          },
        })
        const rawUsers =
          res.data?.results ||
          res.data?.data ||
          res.data?.users ||
          (Array.isArray(res.data) ? res.data : [])
        const matchedUser = rawUsers.find(user => {
          const id = user.id ?? user.user_id ?? user.pk
          const email = (user.email || "").toLowerCase()
          return (
            String(id) === String(currentUserId) ||
            (currentUserEmail && email === currentUserEmail)
          )
        })
        if (matchedUser) {
          const apiRoleName =
            matchedUser.role_name ||
            matchedUser.user_role_name ||
            matchedUser.hrms_role__hrms_role_name ||
            matchedUser.role_data?.role_name ||
            sessionRoleName
          setCurrentRoleName(apiRoleName)
          const apiUserName =
            [matchedUser.first_name, matchedUser.middle_name, matchedUser.last_name]
              .filter(Boolean)
              .join(" ")
              .trim() ||
            matchedUser.username ||
            matchedUser.name ||
            matchedUser.email ||
            sessionUserName
          setCurrentUserName(apiUserName)
        }
      } catch (error) {
        console.error("Could not load current user role from users API", error)
      }
    }

    loadCurrentUserRole()
  }, [companyId, currentUserEmail, currentUserId, sessionRoleName, sessionUserName])

  const getSelectedRecruiterLabel = () => {
    if (selectedRecruiterIds.length !== 1) return ""
    const selectedId = String(selectedRecruiterIds[0])
    return (
      recruiterOptions.find(r => String(r.id) === selectedId)?.label ||
      (recruitersLoading ? "Loading recruiters..." : "") ||
      "Select recruiters"
    )
  }

  const getSelectedJobLabel = () => {
    if (selectedJobIds.length === 0) {
      return jobsLoading ? "Loading job orders..." : "All Job Orders"
    }
    if (selectedJobIds.length === 1) {
      const selectedId = String(selectedJobIds[0])
      return (
        jobOptions.find(job => String(job.id) === selectedId)?.label ||
        "1 job selected"
      )
    }
    return `${selectedJobIds.length} jobs selected`
  }

  useEffect(() => {
    const handleOutsideClick = event => {
      if (
        jobDropdownOpen &&
        jobRef.current &&
        !jobRef.current.contains(event.target)
      ) {
        setJobDropdownOpen(false)
      }
      if (
        recruiterDropdownOpen &&
        recruiterRef.current &&
        !recruiterRef.current.contains(event.target)
      ) {
        setRecruiterDropdownOpen(false)
      }
    }

    window.addEventListener("mousedown", handleOutsideClick)
    return () => window.removeEventListener("mousedown", handleOutsideClick)
  }, [jobDropdownOpen, recruiterDropdownOpen])

  useEffect(() => {
    load()
  }, [load])

  useEffect(() => {
    const t = setTimeout(() => {
      setPage(1)
      setSearch(searchInput)
    }, 400)
    return () => clearTimeout(t)
  }, [searchInput])

  const handleRefresh = () => {
    setSpinning(true)
    if (
      searchInput ||
      search ||
      dateFrom ||
      dateTo ||
      selectedJobIds.length ||
      selectedRecruiterIds.length
    ) {
      setSearchInput("")
      setSearch("")
      setDateFrom("")
      setDateTo("")
      setSelectedJobIds([])
      setJobDropdownOpen(false)
      setSelectedRecruiterIds([])
      setRecruiterDropdownOpen(false)
      setPage(1)
      // state changes above will re-trigger `load` via its own effect
    } else {
      load()
    }
    setTimeout(() => setSpinning(false), 400)
  }

  const clearSearch = () => {
    setSearchInput("")
    setSearch("")
    setPage(1)
  }

  const patchField = async (rowId, field, value) => {
    try {
      await axios.patch(`/api/v1/job-candidates/${rowId}/report-fields/`, {
        [field]: value,
      })
      setRows(prev =>
        prev.map(r => (r.id === rowId ? { ...r, [field]: value } : r))
      )
      if (field === "date_of_joining" && value) {
        const currentRow = rows.find(r => r.id === rowId)
        if (currentRow && currentRow.offer_status !== "Joined") {
          await patchField(rowId, "offer_status", "Joined")
        }
      }
    } catch (e) {
      notify("error", "Could not save the change.")
      load()
    }
  }

  /* -------------------------------------------------------------- */
  /* excel export (unchanged behaviour, still uses ExcelJS)          */
  /* -------------------------------------------------------------- */

  const exportExcel = async () => {
    setExporting(true)
    try {
      const params = { size: 5000 }
      if (search) params.search = search
      if (dateFrom) params.date_from = dateFrom
      if (dateTo) params.date_to = dateTo
      if (selectedJobIds.length > 0) {
        params.job_id = selectedJobIds.join(",")
      }
      if (selectedRecruiterIds.length > 0) {
        params.recruiter_id = selectedRecruiterIds.join(",")
      }
      const res = await axios.get(
        `/api/v1/company/${companyId}/recruiter-submission-report/`,
        { params }
      )
      const data = res.data?.results || []

      const columns = [
        "S. No",
        "Processed Date",
        "Recruiter Name",
        "Entity",
        "Position",
        "Candidate Name",
        "Source",
        "Mobile Number",
        "Email ID",
        "Current Company",
        "Total Exp.",
        "Rel. Exp.",
        "CTC",
        "Exp. CTC",
        "NP",
        "Location",
        "Status",
        "Interview Schedule Date",
        "Offer Status",
        "DOJ",
        "Remarks",
      ]

      const workbook = new ExcelJS.Workbook()
      const sheet = workbook.addWorksheet("Recruiter Tracker Report")
      const colCount = columns.length

      sheet.mergeCells(1, 1, 1, colCount)
      const titleCell = sheet.getCell("A1")
      const effectiveCompanyName =
        companyName ||
        (companyId === "all" ? "All Companies" : "Recruiter Tracker Report") ||
        (userInfos?.user_details?.companies_data || []).find(
          c => String(c.company_id) === String(companyId)
        )?.company_name ||
        "Recruiter Tracker Report"
      const selectedRecruiterLabel =
        selectedRecruiterIds.length === 1
          ? recruiterOptions.find(
              r => String(r.id) === String(selectedRecruiterIds[0])
            )?.label
          : null
      const reportLabel = selectedRecruiterLabel
        ? `${selectedRecruiterLabel}'s Report`
        : "Recruiter Tracker Report"
      titleCell.value =
        effectiveCompanyName === "Recruiter Tracker Report"
          ? reportLabel
          : `${effectiveCompanyName} — ${reportLabel}`
      titleCell.font = { bold: true, size: 14, color: { argb: "FF000000" } }
      titleCell.alignment = { horizontal: "center", vertical: "middle" }
      sheet.getRow(1).height = 30

      const HEADER_ROW = 2
      const headerRow = sheet.getRow(HEADER_ROW)
      columns.forEach((col, i) => {
        const cell = headerRow.getCell(i + 1)
        cell.value = col
        cell.font = { bold: true, size: 11, color: { argb: "FF000000" } }
        cell.alignment = {
          horizontal: "center",
          vertical: "middle",
          wrapText: true,
        }
        cell.border = {
          top: { style: "thin", color: { argb: "FF000000" } },
          left: { style: "thin", color: { argb: "FF000000" } },
          bottom: { style: "thin", color: { argb: "FF000000" } },
          right: { style: "thin", color: { argb: "FF000000" } },
        }
      })
      headerRow.height = 26

      data.forEach((r, idx) => {
        const rowValues = [
          idx + 1,
          fmtDate(r.processed_date),
          displayValue(r.recruiter_name),
          displayValue(r.entity),
          displayValue(r.position),
          displayValue(r.candidate_name),
          displayValue(r.source),
          displayValue(r.mobile_number),
          displayValue(r.email_id),
          displayValue(r.current_company),
          displayValue(r.total_experience),
          displayValue(r.relevant_experience),
          displayValue(r.current_ctc),
          displayValue(r.expected_ctc),
          displayValue(r.notice_period),
          displayValue(r.location),
          displayValue(r.status),
          fmtDate(r.interview_schedule_date),
          displayValue(r.offer_status),
          fmtDate(r.date_of_joining),
          displayValue(r.remarks),
        ]
        const row = sheet.addRow(rowValues)
        row.eachCell({ includeEmpty: true }, cell => {
          cell.border = {
            top: { style: "thin", color: { argb: "FF000000" } },
            left: { style: "thin", color: { argb: "FF000000" } },
            bottom: { style: "thin", color: { argb: "FF000000" } },
            right: { style: "thin", color: { argb: "FF000000" } },
          }
          cell.alignment = { vertical: "middle", wrapText: true }
        })
        if (r.email_id) {
          const emailCell = row.getCell(8)
          emailCell.value = {
            text: r.email_id,
            hyperlink: `mailto:${r.email_id}`,
          }
          emailCell.font = { color: { argb: "FF107C41" }, underline: true }
        }
      })

      columns.forEach((col, i) => {
        const maxLen = Math.max(
          col.length,
          ...data.map(r => String(Object.values(r)[i] ?? "").length)
        )
        sheet.getColumn(i + 1).width = Math.min(Math.max(maxLen + 2, 12), 40)
      })
      sheet.views = [{ state: "frozen", ySplit: HEADER_ROW }]

      const buffer = await workbook.xlsx.writeBuffer()
      saveAs(
        new Blob([buffer]),
        `Recruiter_Submission_Report_${toApiDate(new Date()) || "export"}.xlsx`
      )
      notify("success", "Report exported successfully.")
    } catch (e) {
      notify("error", "Could not export the report.")
    } finally {
      setExporting(false)
    }
  }

  /* -------------------------------------------------------------- */
  /* column definitions for react-data-table-component               */
  /* -------------------------------------------------------------- */

  const columns = [
    {
      name: <span title="Serial Number">S. No</span>,
      width: "64px",
      cell: (r, index) => (page - 1) * perPage + index + 1,
    },
    {
      name: <span title="Processed Date">Processed Date</span>,
      width: "120px",
      sortable: true,
      selector: r => r.processed_date || "",
      cell: r => (
        <span style={styles.plainCell}>{fmtDate(r.processed_date)}</span>
      ),
    },
    {
      name: <span title="Recruiter Name">Recruiter Name</span>,
      width: "140px",
      sortable: true,
      selector: r => displayValue(r.recruiter_name),
      wrap: true,
    },
    {
      name: <span title="Position">Position</span>,
      width: "160px",
      sortable: true,
      selector: r => displayValue(r.position),
      wrap: true,
    },
    {
      name: <span title="Candidate Name & Mobile Number">Candidate Name</span>,
      width: "180px",
      sortable: true,
      selector: r => displayValue(r.candidate_name),
      cell: r => (
        <div>
          <div style={{ fontWeight: 600 }}>{displayValue(r.candidate_name)}</div>
          <div style={{ fontSize: 12, color: colors.textMuted, marginTop: 2 }}>
            {displayValue(r.mobile_number)}
          </div>
        </div>
      ),
      wrap: true,
    },
    {
      name: <span title="Email ID">Email ID</span>,
      width: "210px",
      sortable: true,
      selector: r => displayValue(r.email_id),
      cell: r =>
        r.email_id ? (
          <a href={`mailto:${r.email_id}`} style={styles.link}>
            {r.email_id}
          </a>
        ) : (
          <span title="--">--</span>
        ),
    },
    {
      name: <span title="Source">Source</span>,
      width: "130px",
      sortable: true,
      selector: r => displayValue(r.source),
      wrap: true,
    },
    {
      name: <span title="Current Company">Current Company</span>,
      width: "150px",
      sortable: true,
      selector: r => displayValue(r.current_company),
      cell: r => {
        const currentCompany = displayValue(r.current_company)
        return <span title={currentCompany}>{currentCompany}</span>
      },
      wrap: true,
    },
    {
      name: <span title="Total Experience">Total Exp</span>,
      width: "102px",
      sortable: true,
      selector: r => displayValue(r.total_experience),
      cell: r => {
        const exp = displayValue(r.total_experience)
        return <span>{exp === "--" ? exp : `${exp} years`}</span>
      },
    },
    {
      name: <span title="Relevant Experience">Rel. Exp.</span>,
      width: "110px",
      sortable: true,
      selector: r => displayValue(r.relevant_experience),
      cell: r => (
        <EditableCell
          value={r.relevant_experience}
          onSave={v => patchField(r.id, "relevant_experience", v)}
        />
      ),
    },
    {
      name: <span title="Current CTC - Expected CTC">CTC (Curr-Exp)</span>,
      width: "140px",
      sortable: true,
      selector: r => Number(r.current_ctc) || 0,
      cell: r => (
        <div style={{ display: "flex", alignItems: "center", gap: 4, width: "100%" }}>
          <div style={{ flex: 1 }}>
            <EditableCell
              value={r.current_ctc}
              onSave={v => patchField(r.id, "current_ctc", v)}
            />
          </div>
          <span style={{ color: colors.textMuted, fontWeight: 700 }}>-</span>
          <div style={{ flex: 1 }}>
            <EditableCell
              value={r.expected_ctc}
              onSave={v => patchField(r.id, "expected_ctc", v)}
            />
          </div>
        </div>
      ),
    },
    {
      name: <span title="Notice Period">Notice Period</span>,
      width: "90px",
      sortable: true,
      selector: r => displayValue(r.notice_period),
      cell: r => (
        <EditableCell
          value={r.notice_period}
          onSave={v => patchField(r.id, "notice_period", v)}
        />
      ),
    },
    {
      name: <span title="Location">Location</span>,
      width: "120px",
      sortable: true,
      selector: r => displayValue(r.location),
      cell: r => (
        <EditableCell
          value={r.location}
          onSave={v => patchField(r.id, "location", v)}
        />
      ),
    },
    {
      name: <span title="Status">Status</span>,
      width: "110px",
      sortable: true,
      selector: r => displayValue(r.status),
      cell: r => (
        <EditableCell
          value={r.status}
          onSave={v => patchField(r.id, "status", v)}
        />
      ),
    },
    {
      name: <span title="Offer Status">Offer Status</span>,
      width: "130px",
      sortable: true,
      selector: r => displayValue(r.offer_status),
      cell: r => (
        <EditableCell
          value={r.offer_status}
          type="select"
          options={OFFER_STATUS_OPTIONS}
          onSave={v => patchField(r.id, "offer_status", v)}
        />
      ),
    },
    {
      name: <span title="Date of Joining">DOJ</span>,
      width: "120px",
      sortable: true,
      selector: r => r.date_of_joining || "",
      cell: r => (
        <EditableCell
          value={r.date_of_joining}
          type="date"
          onSave={v => patchField(r.id, "date_of_joining", v)}
        />
      ),
    },
    {
      name: <span title="Interview Schedule Date">Interview Schedule Date</span>,
      width: "160px",
      sortable: true,
      selector: r => r.interview_schedule_date || "",
      cell: r => (
        <EditableCell
          value={r.interview_schedule_date}
          type="date"
          onSave={v => patchField(r.id, "interview_schedule_date", v)}
        />
      ),
    },
    {
      name: <span title="Remarks">Remarks</span>,
      width: "170px",
      sortable: true,
      cell: r => (
        <EditableCell
          value={r.remarks}
          type="textarea"
          onSave={v => patchField(r.id, "remarks", v)}
        />
      ),
    },
  ]

  return (
    <div className="page-content" style={styles.page}>
      {toastMsg && (
        <div style={styles.toast(toastMsg.type)}>{toastMsg.message}</div>
      )}

      <div style={styles.header}>
        <div style={styles.headerLeft}>
          <div style={styles.iconBubble}>📋</div>
          <div>
            <h2 style={styles.title}>Recruiter Tracker Report</h2>
            <p style={styles.subtitle}>
              Every candidate a recruiter submits to a job appears here
              automatically — edit any cell to update it.
            </p>
          </div>
        </div>
        <button
          style={styles.exportBtn(exporting, !companyId)}
          onClick={exportExcel}
          disabled={!companyId || exporting}
        >
          {exporting ? "Exporting…" : "⬇ Export to Excel"}
        </button>
      </div>

      <div style={styles.panel}>
        <div style={styles.toolbar}>
          <div style={styles.searchWrap}>
            <span style={styles.searchIcon}>🔍</span>
            <input
              style={styles.searchInput}
              value={searchInput}
              onChange={e => setSearchInput(e.target.value)}
              placeholder="Search candidate, email, phone…"
            />
            {searchInput && (
              <button
                style={styles.clearBtn}
                onClick={clearSearch}
                title="Clear search"
                type="button"
              >
                ✕
              </button>
            )}
          </div>

          <div style={{ ...styles.multiselectWrapper, flex: 1 }} ref={jobRef}>
            <div
              role="button"
              tabIndex={0}
              style={{
                ...styles.multiselectButton,
                minHeight: selectedJobIds.length > 0 ? 44 : 40,
                height: "auto",
                cursor: !companyId || jobsLoading ? "not-allowed" : "pointer",
              }}
              title={
                selectedJobIds.length > 0
                  ? jobOptions
                      .filter(job => selectedJobIds.includes(String(job.id)))
                      .map(job => job.label)
                      .join(", ")
                  : undefined
              }
              onClick={() => {
                if (!companyId || jobsLoading) return
                setJobDropdownOpen(open => !open)
              }}
              onKeyDown={event => {
                if (event.key === "Enter" || event.key === " ") {
                  event.preventDefault()
                  if (!companyId || jobsLoading) return
                  setJobDropdownOpen(open => !open)
                }
              }}
            >
              {getSelectedJobLabel()}
              <span style={styles.caret}>▾</span>
            </div>
            {jobDropdownOpen && (
              <div style={styles.multiselectPanel}>
                <label style={styles.multiselectItem}>
                  <input
                    type="checkbox"
                    checked={selectedJobIds.length === 0}
                    onChange={() => {
                      setPage(1)
                      setSelectedJobIds([])
                      setJobDropdownOpen(false)
                    }}
                  />
                  <span>All Job Orders</span>
                </label>
                {jobOptions.map(job => (
                  <label key={job.id} style={styles.multiselectItem}>
                    <input
                      type="checkbox"
                      checked={selectedJobIds.includes(String(job.id))}
                      onChange={() => {
                        setPage(1)
                        setSelectedJobIds(prev => {
                          const jobId = String(job.id)
                          const exists = prev.includes(jobId)
                          return exists
                            ? prev.filter(id => id !== jobId)
                            : [...prev, jobId]
                        })
                      }}
                    />
                    <span>{job.label}</span>
                  </label>
                ))}
              </div>
            )}
          </div>

          <div style={{ ...styles.multiselectWrapper, flex: 1 }} ref={recruiterRef}>
            <button
              type="button"
              style={styles.multiselectButton}
              title={
                selectedRecruiterIds.length > 1
                  ? recruiterOptions
                      .filter(r => selectedRecruiterIds.includes(String(r.id)))
                      .map(r => r.label)
                      .join(", ")
                  : undefined
              }
              onClick={
                canSelectMultipleRecruiters
                  ? () => setRecruiterDropdownOpen(open => !open)
                  : undefined
              }
              disabled={!canSelectMultipleRecruiters}
            >
              {!isSuperAdmin
                ? currentUserName
                : selectedRecruiterIds.length === 1
                ? getSelectedRecruiterLabel()
                : selectedRecruiterIds.length > 1
                ? `${selectedRecruiterIds.length} selected`
                : recruitersLoading
                ? "Loading recruiters…"
                : "Select recruiters"}
              <span style={styles.caret}>▾</span>
            </button>
            {isSuperAdmin && recruiterDropdownOpen && (
              <div style={styles.multiselectPanel}>
                {recruiterOptions.map(recruiter => (
                  <label key={recruiter.id} style={styles.multiselectItem}>
                    <input
                      type="checkbox"
                      checked={selectedRecruiterIds.includes(String(recruiter.id))}
                      onChange={() => {
                        setPage(1)
                        setSelectedRecruiterIds(prev => {
                          const recruiterId = String(recruiter.id)
                          const exists = prev.includes(recruiterId)
                          return exists
                            ? prev.filter(id => id !== recruiterId)
                            : [...prev, recruiterId]
                        })
                      }}
                    />
                    <span>{recruiter.label}</span>
                  </label>
                ))}
              </div>
            )}
          </div>

          <input
            style={{ ...styles.dateInput, flex: 1 }}
            type="date"
            value={dateFrom}
            max={dateTo ? addDaysToDateString(dateTo, -1) : undefined}
            onChange={e => {
              setPage(1)
              setDateFrom(e.target.value)
            }}
          />
          <input
            style={{ ...styles.dateInput, flex: 1 }}
            type="date"
            value={dateTo}
            min={dateFrom ? addDaysToDateString(dateFrom, 1) : undefined}
            onChange={e => {
              setPage(1)
              setDateTo(e.target.value)
            }}
          />

          <div style={styles.companyWrap}>
            <CompanySelector
              showAllOption
              selectedCompanyId={companyId}
              onCompanyChange={company => {
                setPage(1)
                const isAll =
                  String(company?.company_id).toLowerCase() === "all"
                setCompanyId(isAll ? "all" : company?.company_id ?? null)
                setCompanyName(
                  isAll
                    ? "All Companies"
                    : company?.company_name || company?.name || ""
                )
                setSelectedJobIds([])
                setJobDropdownOpen(false)
                setSelectedRecruiterIds([])
                setRecruiterDropdownOpen(false)
              }}
            />
          </div>

          <button
            style={styles.refreshBtn(spinning)}
            onClick={handleRefresh}
            title="Refresh & clear filters"
          >
            ⟳
          </button>
        </div>

        <div id="RecruiterTrackerReport_TableView">
            <DataTable
            columns={columns}
            data={rows}
            progressPending={loading}
            progressComponent={<ShimmerTable rowCount={perPage > 8 ? 8 : perPage} />}
            customStyles={customTableStyles}
            pagination
            paginationServer
            paginationTotalRows={total}
            paginationDefaultPage={page}
            paginationPerPage={perPage}
            paginationRowsPerPageOptions={PAGE_SIZE_OPTIONS}
            paginationComponentOptions={{
                rowsPerPageText: "Rows per page:",
                rangeSeparatorText: "of",
                selectAllRowsItem: true,
                selectAllRowsItemText: "All",
            }}
            onChangePage={p => setPage(p)}
            onChangeRowsPerPage={(newPerPage, p) => {
                setPerPage(newPerPage)
                setPage(p)
            }}
            noDataComponent={
                <div style={{ padding: 24, color: colors.textMuted }}>
                No Records found.
                </div>
            }
            highlightOnHover
            striped
            persistTableHead
            responsive
            />
        </div>
      </div>
    </div>
  )
}